import GlobalStyles from '@mui/joy/GlobalStyles';
import Box from '@mui/joy/Box';
import List from '@mui/joy/List';
import { listItemButtonClasses } from '@mui/joy/ListItemButton';
import Sheet from '@mui/joy/Sheet';
import { FC, JSX } from 'react';
import { default as useUserPermissions } from '@consolidocs-ui/hooks/useUserPermissions';
import SidebarMenuLoader from './SidebarMenuLoader/SidebarMenuLoader';
import useRoutes from '_routes/hooks/useRoutes';
import SidebarItem from './SidebarItem';
import './Sidebar.scss';

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
}

const Sidebar: FC<SidebarProps> = ({ open = false, onClose }): JSX.Element => {
  const { isFetching: isLoadingPermissions } = useUserPermissions();
  const { routes } = useRoutes();
  const sidebarItems = routes as any[];

  return (
    <>
      <Sheet
        className="Sidebar"
        sx={{
          position: { xs: 'fixed', md: 'sticky' },
          transform: {
            xs: open ? 'translateX(0)' : 'translateX(-100%)',
            md: 'none',
          },
          transition: 'transform 0.4s, width 0.4s',
          zIndex: 10000,
          height: { xs: '100vh', md: 'calc(100vh - 48px)' },
          overflowY: 'auto',
          top: { xs: '48px', md: 0 },
          left: 0,
        }}
      >
        <GlobalStyles
          styles={(theme) => ({
            ':root': {
              '--Sidebar-width': '220px',
              [theme.breakpoints.up('lg')]: {
                '--Sidebar-width': '240px',
              },
              [theme.breakpoints.down('sm')]: {
                '--Sidebar-width': '200px',
              },
              [theme.breakpoints.down('xs')]: {
                '--Sidebar-width': '160px',
              },
            },
          })}
        />
        <Box
          sx={{
            minHeight: 0,
            overflow: 'hidden auto',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            [`& .${listItemButtonClasses.root}`]: {
              gap: { xs: 0.75, sm: 1.5 },
            },
          }}
        >
          <List
            size="sm"
            sx={{
              gap: 1,
              '--List-nestedInsetStart': '30px',
              '--ListItem-radius': (theme) => theme.vars.radius.sm,
            }}
          >
            {isLoadingPermissions ? (
              <SidebarMenuLoader />
            ) : (
              sidebarItems.map((item, index) => {
                return (
                  <SidebarItem item={item} key={index} onNavigate={onClose} />
                );
              })
            )}
          </List>
        </Box>
      </Sheet>
      <Box
        className={`Sidebar-overlay${open ? ' open' : ''}`}
        sx={{ display: { md: 'none' } }}
        onClick={onClose}
      />
    </>
  );
};

export default Sidebar;
